/* ===========================================================
   Hero slider behavior — v2
   - Auto-play (pause on hover/hidden tab)
   - Manual prev/next + dots
   - Touch swipe (mobile) AND mouse drag (desktop)
   - Video: only the active slide plays; others paused for perf
   - Click suppression after a real drag so links don't fire
   - Respects prefers-reduced-motion
   =========================================================== */

(function () {
  'use strict';

  const heroes = document.querySelectorAll('[data-hero]');
  if (!heroes.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  heroes.forEach((hero) => {
    const track = hero.querySelector('[data-hero-track]');
    const slides = hero.querySelectorAll('[data-hero-slide]');
    const prevBtn = hero.querySelector('[data-hero-prev]');
    const nextBtn = hero.querySelector('[data-hero-next]');
    const dots = hero.querySelectorAll('[data-hero-dot]');

    if (!track || slides.length === 0) return;

    const total = slides.length;
    let current = 0;
    let timer = null;
    const autoplay = hero.dataset.autoplay === 'true' && !prefersReducedMotion && total > 1;
    const interval = parseInt(hero.dataset.interval, 10) || 5000;

    /* ============ Video management ============ */
    const allVideos = hero.querySelectorAll('[data-hero-video]');

    const playActiveVideos = () => {
      slides.forEach((slide, i) => {
        const videos = slide.querySelectorAll('[data-hero-video]');
        videos.forEach((v) => {
          if (i === current) {
            // Bump preload on the active slide so it actually plays
            if (v.preload !== 'auto') v.preload = 'auto';
            // Only call play() if not already playing — avoids unnecessary promise rejections
            if (v.paused) {
              const p = v.play();
              if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay blocked is fine */ });
            }
          } else {
            if (!v.paused) v.pause();
          }
        });
      });
    };

    /* ============ Slide change ============ */
    const goTo = (index) => {
      current = (index + total) % total;
      track.style.transform = `translateX(-${current * 100}%)`;
      dots.forEach((dot, i) => {
        const active = i === current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      playActiveVideos();
    };

    const next = () => goTo(current + 1);
    const prev = () => goTo(current - 1);

    /* ============ Auto-play ============ */
    const start = () => {
      if (!autoplay) return;
      stop();
      timer = setInterval(next, interval);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    /* ============ Manual controls ============ */
    nextBtn?.addEventListener('click', () => { next(); start(); });
    prevBtn?.addEventListener('click', () => { prev(); start(); });
    dots.forEach((dot) =>
      dot.addEventListener('click', () => {
        const i = parseInt(dot.dataset.heroDot, 10);
        if (!isNaN(i)) { goTo(i); start(); }
      })
    );

    /* ============ Hover pause / visibility ============ */
    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop(); else start();
    });

    /* ============ Touch swipe (mobile) ============ */
    let touchStartX = 0;
    let touchDeltaX = 0;
    let isTouching = false;
    const TOUCH_THRESHOLD = 40;

    track.addEventListener('touchstart', (e) => {
      if (!e.touches?.length) return;
      touchStartX = e.touches[0].clientX;
      touchDeltaX = 0;
      isTouching = true;
      stop();
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      if (!isTouching || !e.touches?.length) return;
      touchDeltaX = e.touches[0].clientX - touchStartX;
    }, { passive: true });

    track.addEventListener('touchend', () => {
      if (!isTouching) return;
      isTouching = false;
      if (Math.abs(touchDeltaX) > TOUCH_THRESHOLD) {
        if (touchDeltaX < 0) next(); else prev();
      }
      start();
    }, { passive: true });

    /* ============ Mouse drag (desktop) ============ */
    let isMouseDown = false;
    let mouseStartX = 0;
    let mouseDeltaX = 0;
    let didDrag = false;
    const MOUSE_THRESHOLD = 50;
    const DRAG_NOISE = 5; // ignore tiny mouse jitter

    const onMouseDown = (e) => {
      // Only left click, ignore drags starting on form fields / buttons / links
      if (e.button !== 0) return;
      // If user clicked the product card or arrows/dots, let those handle it
      if (e.target.closest('[data-hero-prev], [data-hero-next], [data-hero-dot]')) return;

      isMouseDown = true;
      mouseStartX = e.clientX;
      mouseDeltaX = 0;
      didDrag = false;
      hero.classList.add('is-grabbing');
      stop();
    };

    const onMouseMove = (e) => {
      if (!isMouseDown) return;
      mouseDeltaX = e.clientX - mouseStartX;
      if (Math.abs(mouseDeltaX) > DRAG_NOISE) didDrag = true;
    };

    const onMouseUp = () => {
      if (!isMouseDown) return;
      isMouseDown = false;
      hero.classList.remove('is-grabbing');
      if (Math.abs(mouseDeltaX) > MOUSE_THRESHOLD) {
        if (mouseDeltaX < 0) next(); else prev();
      }
      start();
      // Reset drag flag shortly after — used by click suppression below
      setTimeout(() => { didDrag = false; }, 50);
    };

    track.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    // Block native image drag (causes ghost image)
    track.addEventListener('dragstart', (e) => e.preventDefault());

    // Suppress click on links/cards if user dragged
    track.addEventListener('click', (e) => {
      if (didDrag) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    /* ============ Keyboard ============ */
    hero.setAttribute('tabindex', '0');
    hero.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { next(); start(); }
      if (e.key === 'ArrowLeft')  { prev(); start(); }
    });

    /* ============ Init ============ */
    goTo(0);
    start();
  });
})();
